import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Stack,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { Page } from 'components/Shared/Page'
import { formatISO, isAfter, isValid } from 'date-fns'
import { useApiClient } from 'hooks/useApiClient'
import {
  FormProvider,
  useForm,
  type Control,
  useFieldArray,
} from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'stores/useSnackbar'
import { array, date, number, object, string } from 'yup'
import { PageHeader } from 'components/Shared/PageHeader'
import { TextField } from 'components/Shared/TextField'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { isPresent } from '@jjvgaming/player-payback-library'
import { AdornedDateTimeField } from 'components/AdornedDateTimeField/AdornedDateTimeField'
import { useWatch } from 'react-hook-form'
import { isNil } from 'lodash'
import { LocationSelect } from '../../../components/Shared/LocationSelect'
import { SelectCorporateAccount } from 'components/SelectCorporateAccount'
import { SelectFromAllOrganizations } from 'components/SelectFromAllOrganizations'
import { ContestSubtypeSelect } from 'components/Shared/ContestSubtypeSelect'
import { useEffect, useState, useMemo } from 'react'
import { type ContestSubtype } from 'types/api'

const today = new Date()
today.setHours(0, 0, 0, 0)

const isLocationBasedSubtype = (subtype?: string) =>
  subtype === 'Location' || subtype === 'CombinedLocations'

type FormSchemaType<T> = T extends Control<infer R> ? R : never

export const LocationSweepstakesNewPage = () => {
  const { post } = useApiClient()
  const setMessage = useSnackbar((state) => state.setMessage)
  const navigate = useNavigate()
  const [isMultiDraw, setIsMultiDraw] = useState(false)
  const [numberOfDrawings, setNumberOfDrawings] = useState(2)

  const getSweepstakeSchema = (isMultiDraw: boolean) =>
    object().shape({
      Sweepstake: object({
        name: string().required('Must enter a sweepstake name'),
        startDate: date()
          .typeError('Must enter a valid start date')
          .required('Must enter a start date')
          .when([], {
            is: () => !isMultiDraw,
            then: (schema) => schema.required('Must enter a start date'),
            otherwise: (schema) => schema.notRequired(),
          })
          .test(
            'DayInPast',
            'Start date should not be a date in the past',
            (value) => {
              return !isNil(value) && value.toISOString()
                ? isAfter(value, new Date())
                : true
            }
          ),
        endDate: date()
          .typeError('Must enter a valid end date')
          .required('Must enter an end date')
          .when([], {
            is: () => !isMultiDraw,
            then: (schema) => schema.required('Must enter an end date'),
            otherwise: (schema) => schema.notRequired(),
          })
          .when(['startDate'], ([startDate], schema) => {
            if (isValid(startDate)) {
              return schema.min(
                startDate,
                'End date should not be before start date'
              )
            }
            return schema
          }),
        drawings: array().of(
          object().shape({
            startDate: date()
              .typeError('Must enter a valid start date')
              .required('Must enter a start date')
              .when([], {
                is: () => isMultiDraw,
                then: (schema) => schema.required('Must enter a start date'),
                otherwise: (schema) => schema.notRequired(),
              }),
            endDate: date()
              .typeError('Must enter a valid end date')
              .required('Must enter a end date')
              .when([], {
                is: () => isMultiDraw,
                then: (schema) => schema.required('Must enter an end date'),
                otherwise: (schema) => schema.notRequired(),
              }),
          })
        ),
        description: string()
          .required('Must enter a sweepstake description')
          .max(500),
        locationSweepstakeType: string().required(
          'Must select a sweepstake type'
        ),
        numLocationWinners: number()
          .required('Must enter a number of winners per drawing')
          .transform((val) => (Number.isNaN(val) ? undefined : val))
          .min(1, 'Number of Winners must be greater than or equal to 1')
          .max(99, 'Number of Winners must be less than 100'),
        numAffidavitDays: number()
          .required('Must enter a number of days to complete affidavit')
          .transform((val) => (Number.isNaN(val) ? undefined : val))
          .min(1, 'Number of days must be greater than or equal to 1')
          .max(99, 'Number of days must be less than 100'),
        marketingSpecialistEmail: string()
          .email('Must enter a valid email')
          .nullable()
          .notRequired()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value
          ),
        licensedEstablishmentIds: array()
          .of(number())
          .transform((value, originalValue) =>
            Array.isArray(originalValue) && originalValue.length === 0
              ? null
              : value
          )
          .when('locationSweepstakeType', {
            is: (type: string) => isLocationBasedSubtype(type),
            then: () =>
              array()
                .of(number())
                .min(1, 'Must select at least one Licensed Establishment')
                .required('Must select at least one Licensed Establishment'),
            otherwise: () => array().of(number()).nullable(),
          }),
        corporateAccountId: number().when('locationSweepstakeType', {
          is: (type: string) => type === 'CorporateAccount',
          then: () => number().required('Must select a Corporate Account'),
          otherwise: () => number().nullable(),
        }),
        organizationId: number().when('locationSweepstakeType', {
          is: (type: string) => type === 'Organization',
          then: () => number().required('Must select an Organization'),
          otherwise: () => number().nullable(),
        }),
      }),
    })

  const schema = useMemo(() => getSweepstakeSchema(isMultiDraw), [isMultiDraw])

  const formMethods = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      Sweepstake: {
        locationSweepstakeType: '',
        drawings: [],
      },
    },
  })

  const {
    watch,
    clearErrors,
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = formMethods

  const locationSweepstakeType = useWatch({
    control,
    name: 'Sweepstake.locationSweepstakeType',
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'Sweepstake.drawings',
  })

  const selectedLocations = watch('Sweepstake.licensedEstablishmentIds')

  // clear fields when locationSweepstakeType changes
  useEffect(() => {
    formMethods.setValue('Sweepstake.corporateAccountId', undefined)
    formMethods.setValue('Sweepstake.organizationId', undefined)
    formMethods.setValue('Sweepstake.licensedEstablishmentIds', [])
  }, [locationSweepstakeType])

  // clear licensedEstablishmentIds error after LEs are selected
  useEffect(() => {
    if (
      isLocationBasedSubtype(locationSweepstakeType) &&
      selectedLocations &&
      selectedLocations?.length > 0
    ) {
      clearErrors('Sweepstake.licensedEstablishmentIds')
    }
  }, [selectedLocations, locationSweepstakeType, clearErrors])

  useEffect(() => {
    const currentLength = watch('Sweepstake.drawings')?.length ?? 0
    if (numberOfDrawings > currentLength) {
      for (let i = currentLength; i < numberOfDrawings; i++) {
        // @ts-expect-error we know start and end date can't be null but want to default them null here to force user to complete them
        append({ startDate: null, endDate: null })
      }
    } else if (numberOfDrawings < currentLength) {
      for (let i = currentLength; i > numberOfDrawings; i--) {
        remove(i - 1)
      }
    }
  }, [numberOfDrawings])

  const createSweepstake = useMutation({
    mutationKey: ['sweepstakes/create'],
    mutationFn: async (data: FormSchemaType<typeof control>) => {
      const response = await post('/admin/contests', {
        params: { header: undefined },
        body: {
          type: 'LocationSweepstake',
          subtype: data.Sweepstake.locationSweepstakeType as ContestSubtype,
          name: data.Sweepstake.name,
          description: data.Sweepstake.description,
          startDate: formatISO(data.Sweepstake.startDate),
          endDate: formatISO(data.Sweepstake.endDate),
          grandPrizeType: 'None',
          licensedEstablishmentIds:
            Array.isArray(data.Sweepstake.licensedEstablishmentIds) &&
            data.Sweepstake.licensedEstablishmentIds.length > 0
              ? data.Sweepstake.licensedEstablishmentIds.filter(
                  (id: number | undefined): id is number =>
                    typeof id === 'number'
                )
              : null,
          corporateAccountId: data.Sweepstake.corporateAccountId,
          organizationId: data.Sweepstake.organizationId,
          numLocationWinners: data.Sweepstake.numLocationWinners,
          numAffidavitDays: data.Sweepstake.numAffidavitDays,
          marketingSpecialistEmail:
            data.Sweepstake.marketingSpecialistEmail ?? undefined,
        },
      })
      return response
    },
    onSuccess: async (response) => {
      setMessage('Sweepstake successfully created', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      navigate(`/Contests/LocationSweepstakes/${response.data?.id}/Overview`)
    },
    onError: async () => {
      setMessage('Error creating sweepstake', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  const createMultipleSweepstake = useMutation({
    mutationKey: ['sweepstakes/createMultiple'],
    mutationFn: async (data: FormSchemaType<typeof control>) => {
      const response = await post('/admin/contests/multiDrawing', {
        params: { header: undefined },
        body: {
          type: 'LocationSweepstake',
          subtype: data.Sweepstake.locationSweepstakeType as ContestSubtype,
          name: data.Sweepstake.name,
          description: data.Sweepstake.description,
          drawings: data.Sweepstake.drawings?.map((drawing) => ({
            startDate: formatISO(drawing.startDate),
            endDate: formatISO(drawing.endDate),
          })),
          grandPrizeType: 'None',
          licensedEstablishmentIds:
            Array.isArray(data.Sweepstake.licensedEstablishmentIds) &&
            data.Sweepstake.licensedEstablishmentIds.length > 0
              ? data.Sweepstake.licensedEstablishmentIds.filter(
                  (id: number | undefined): id is number =>
                    typeof id === 'number'
                )
              : null,
          corporateAccountId: data.Sweepstake.corporateAccountId,
          organizationId: data.Sweepstake.organizationId,
          numLocationWinners: data.Sweepstake.numLocationWinners,
          numAffidavitDays: data.Sweepstake.numAffidavitDays,
          marketingSpecialistEmail:
            data.Sweepstake.marketingSpecialistEmail ?? undefined,
        },
      })
      return response
    },
    onSuccess: async (response) => {
      setMessage('Sweepstake successfully created', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      navigate(
        `/Contests/LocationSweepstakes/${Number(response.data?.id)}/Overview`
      )
    },
    onError: async () => {
      setMessage('Error creating sweepstake', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  const createSweepstakeWrapper = handleSubmit((data) => {
    isMultiDraw
      ? createMultipleSweepstake.mutate(data)
      : createSweepstake.mutate(data)
  })

  return (
    <Page
      header={
        <PageHeader
          title="Create a New Location Sweepstake"
          backPath="/Contests/LocationSweepstakes"
          backText="Location Sweepstakes"
          isSecondary={true}
          subtitle={`You must fill out every field in the form before clicking the submit button.\nIncomplete submissions cannot be processed.`}
        />
      }
    >
      <FormProvider {...formMethods}>
        <form onSubmit={createSweepstakeWrapper} noValidate>
          <Stack spacing={4}>
            <Box>
              <TextField
                required
                error={isPresent(errors.Sweepstake?.name)}
                label="Sweepstake Name"
                placeholder="Enter sweepstake name"
                {...register('Sweepstake.name')}
              />
              {errors.Sweepstake?.name?.message && (
                <FormFieldErrorMessage
                  message={errors.Sweepstake?.name.message}
                />
              )}
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isMultiDraw}
                    onChange={(e) => {
                      setIsMultiDraw(e.target.checked)
                      if (!e.target.checked) {
                        clearErrors('Sweepstake.drawings')
                        setNumberOfDrawings(2)
                      }
                    }}
                  />
                }
                label="Multi-Draw Sweepstakes"
              />
            </Box>

            {isMultiDraw && (
              <>
                <Box
                  sx={{
                    maxWidth: '520px',
                  }}
                >
                  <Box>
                    <InputLabel sx={{ fontWeight: 'normal' }}>
                      Number of Drawings
                    </InputLabel>
                  </Box>
                  <Box>
                    <TextField
                      data-testid="NumberOfDrawings"
                      inputProps={{ min: 2, max: 20 }}
                      fullWidth
                      type="number"
                      placeholder={`Enter # of Drawings for this Sweepstakes`}
                      defaultValue={2}
                      onChange={(e) => {
                        if (
                          Number(e.target.value) < 2 ||
                          Number(e.target.value) > 20
                        ) {
                          setError('Sweepstake.drawings', {
                            type: 'manual',
                            message:
                              'Number of Drawings must be between 2 and 20',
                          })
                          return
                        }
                        clearErrors('Sweepstake.drawings')
                        setNumberOfDrawings(Number(e.target.value))
                      }}
                    />
                    {errors.Sweepstake?.drawings?.message && (
                      <FormFieldErrorMessage
                        message={errors.Sweepstake.drawings.message}
                      />
                    )}
                  </Box>
                </Box>
                {fields.map((field, index) => (
                  <Box key={field.id} mb={3}>
                    Drawing {index + 1}
                    <Box display="flex" gap={2} sx={{ maxWidth: '520px' }}>
                      <Box flex={1}>
                        <AdornedDateTimeField
                          name={`Sweepstake.drawings.${index}.startDate`}
                          control={control}
                          label="Start Date/Time"
                        />
                        {errors.Sweepstake?.drawings?.[index]?.startDate && (
                          <FormFieldErrorMessage
                            message={String(
                              errors.Sweepstake.drawings[index].startDate
                                .message
                            )}
                          />
                        )}
                      </Box>
                      <Box flex={1}>
                        <AdornedDateTimeField
                          name={`Sweepstake.drawings.${index}.endDate`}
                          control={control}
                          label="End Date/Time"
                        />
                        {errors.Sweepstake?.drawings?.[index]?.endDate && (
                          <FormFieldErrorMessage
                            message={String(
                              errors.Sweepstake.drawings[index].endDate.message
                            )}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </>
            )}

            {!isMultiDraw && (
              <>
                <Box>
                  <AdornedDateTimeField
                    name="Sweepstake.startDate"
                    control={control}
                    label="Start Date/Time"
                  />
                  {errors.Sweepstake?.startDate?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake?.startDate?.message}
                    />
                  )}
                </Box>
                <Box>
                  <AdornedDateTimeField
                    name="Sweepstake.endDate"
                    control={control}
                    label="End Date/Time"
                  />
                  {errors.Sweepstake?.endDate?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake?.endDate.message}
                    />
                  )}
                </Box>
              </>
            )}
            <Box>
              <TextField
                multiline
                rows={3}
                required
                error={isPresent(errors.Sweepstake?.description)}
                label="Sweepstake Overview"
                placeholder="Description of sweepstake"
                inputProps={{ maxLength: 500 }}
                {...register('Sweepstake.description')}
              />
              {errors.Sweepstake?.description?.message && (
                <FormFieldErrorMessage
                  message={errors.Sweepstake?.description.message}
                />
              )}
            </Box>

            <Box
              sx={{
                maxWidth: '520px',
              }}
            >
              <Box>
                <InputLabel
                  required
                  sx={{ fontWeight: 'normal' }}
                  id="Sweepstake.type"
                >
                  Type
                </InputLabel>
              </Box>
              <Box>
                <ContestSubtypeSelect
                  name="Sweepstake.locationSweepstakeType"
                  data-testid="SweepstakeTypeSelect"
                  types={[
                    'Location',
                    'CombinedLocations',
                    'CorporateAccount',
                    'Organization',
                  ]}
                  disabled={false}
                />

                {errors.Sweepstake?.locationSweepstakeType?.message && (
                  <FormFieldErrorMessage
                    message={errors.Sweepstake?.locationSweepstakeType?.message}
                  />
                )}
              </Box>
            </Box>

            {isLocationBasedSubtype(locationSweepstakeType) && (
              <Box>
                <LocationSelect
                  required
                  showLabel
                  data-testid="LocationSelect"
                  {...register('Sweepstake.licensedEstablishmentIds')}
                />
                {isLocationBasedSubtype(locationSweepstakeType) &&
                  errors.Sweepstake?.licensedEstablishmentIds?.message && (
                    <FormFieldErrorMessage
                      message={
                        errors.Sweepstake?.licensedEstablishmentIds?.message
                      }
                    />
                  )}
              </Box>
            )}

            {locationSweepstakeType === 'CorporateAccount' && (
              <Box>
                <SelectCorporateAccount
                  data-testid="SelectCorporateAccount"
                  required
                  error={isPresent(errors.Sweepstake?.corporateAccountId)}
                  {...register('Sweepstake.corporateAccountId')}
                  activityIndicatorMaxWidth="520px"
                  label="Corporate Account"
                  fullWidth
                  corporateAccountIdKey="corporateAccountId"
                  placeholder="Select a Corporate Account"
                />
                {locationSweepstakeType === 'CorporateAccount' &&
                  errors.Sweepstake?.corporateAccountId?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake?.corporateAccountId?.message}
                    />
                  )}
              </Box>
            )}

            {locationSweepstakeType === 'Organization' && (
              <Box>
                <SelectFromAllOrganizations
                  {...register('Sweepstake.organizationId')}
                  activityIndicatorMaxWidth="520px"
                  required
                  error={isPresent(errors.Sweepstake?.organizationId)}
                  label="Organization"
                  fullWidth
                  organizationIdKey="organizationId"
                  placeholder="Select an Organization"
                />
                {locationSweepstakeType === 'Organization' &&
                  errors.Sweepstake?.organizationId?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake?.organizationId?.message}
                    />
                  )}
              </Box>
            )}

            <Box
              sx={{
                maxWidth: '520px',
              }}
            >
              <Box>
                <InputLabel
                  required
                  sx={{ fontWeight: 'normal' }}
                  id="Sweepstake.numLocationWinners"
                >
                  Number of Winners per Drawing
                </InputLabel>
              </Box>
              <Box>
                <TextField
                  data-testid="NumberOfLocationWinners"
                  InputProps={{ inputProps: { min: 0 } }}
                  fullWidth
                  {...register('Sweepstake.numLocationWinners', {
                    required: true,
                  })}
                  type="number"
                  placeholder={`Enter # of Winners per Drawing`}
                  error={isPresent(errors.Sweepstake?.numLocationWinners)}
                />
                {errors.Sweepstake?.numLocationWinners?.message && (
                  <FormFieldErrorMessage
                    message={errors.Sweepstake.numLocationWinners.message}
                  />
                )}
              </Box>
            </Box>

            <Box
              sx={{
                maxWidth: '520px',
              }}
            >
              <Box>
                <InputLabel
                  required
                  sx={{ fontWeight: 'normal' }}
                  id="Sweepstake.numAffidavitDays"
                >
                  Claim Period
                </InputLabel>
              </Box>
              <Box>
                <TextField
                  data-testid="NumberOfAffidavitDays"
                  InputProps={{ inputProps: { min: 0 } }}
                  fullWidth
                  {...register('Sweepstake.numAffidavitDays', {
                    required: true,
                  })}
                  type="number"
                  placeholder={`Enter # of Days to Complete Affidavit`}
                  error={isPresent(errors.Sweepstake?.numAffidavitDays)}
                />
                {errors.Sweepstake?.numAffidavitDays?.message && (
                  <FormFieldErrorMessage
                    message={errors.Sweepstake.numAffidavitDays.message}
                  />
                )}
              </Box>
            </Box>

            <Box>
              <InputLabel
                sx={{ fontWeight: 'normal' }}
                id="Sweepstake.marketingSpecialistEmail"
              >
                Marketing Specialist Email
              </InputLabel>
              <TextField
                error={isPresent(errors.Sweepstake?.marketingSpecialistEmail)}
                label=""
                placeholder="Enter marketing specialist email"
                {...register('Sweepstake.marketingSpecialistEmail')}
              />
              {errors.Sweepstake?.marketingSpecialistEmail?.message && (
                <FormFieldErrorMessage
                  message={errors.Sweepstake.marketingSpecialistEmail.message}
                />
              )}
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                disabled={createSweepstake.isPending}
              >
                Submit
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  navigate('/Contests/LocationSweepstakes')
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </Page>
  )
}
